/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, forwardRef, HostBinding, inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { CompanyCountryDropdownService } from "./company-country-dropdown.service";
import { Observable, Subject, takeUntil } from "rxjs";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CountryStoreFacade, CountryVoModel } from "@well/common-libs/country-management/shell-api";

@Component({
  selector: 'well-company-country-dropdown',
  templateUrl: './company-country-dropdown.component.html',
  styleUrls: ['./company-country-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CompanyCountryDropdownComponent),
      multi: true,
    }
  ],
})
export class CompanyCountryDropdownComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private readonly countryStoreFacade = inject(CountryStoreFacade);

  private countries$: Observable<CountryVoModel[]> = this.countryStoreFacade.countries$;

  @Input() label: string | undefined = undefined;
  @Input() floatLabelVariant: 'in' | 'over' | 'on' = 'on';

  countriesSuggestions: CountryVoModel[] = [];
  _selectedCountry: CountryVoModel | null = null;

  get selectedCountry(): CountryVoModel | null {
    return this._selectedCountry;
  }

  set selectedCountry(value: CountryVoModel | null) {
    this.filled = !!value;
    this._selectedCountry = value;
  }

  private destroy$: Subject<boolean> = new Subject();

  @Output() countryChange = new EventEmitter<string | null>();
  private countries: CountryVoModel[] = [];
  disabled = false;
  private onChangeCallback: any | undefined = undefined;
  private onTouchedCallback: any | undefined = undefined;
  @HostBinding('class.p-inputwrapper-focus') focus = false;
  @HostBinding('class.p-inputwrapper-filled') filled = false;

  constructor(
    private companyCountryDropdownService: CompanyCountryDropdownService
  ) {}

  ngOnInit() {
    this.countries$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.countries = res;
    });
  }

  onSelectCountry() {
    this.countryChange.emit(this.selectedCountry?.name);
    this.onChange();
  }

  onQueryChange(query: string) {
    const result = this.countries.filter((option) => option.name?.toLowerCase().includes(query.toLowerCase()));
    this.countriesSuggestions = result;
  }

  getDisplayName = (item: CountryVoModel | null): string => {
    return item?.name ?? '';
  };

  compareByName = (a: CountryVoModel, b: CountryVoModel): boolean => {
    return a?.name === b?.name;
  };

  onClear() {
    this.selectedCountry = null;
    this.countryChange.emit(null);
    this.onChange();
  }

  onChange() {
    if (this.onChangeCallback) {
      this.onChangeCallback(this.selectedCountry);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  writeValue(resource: CountryVoModel | null): void {
    if (!resource) {
      this.selectedCountry = null;
    }
    if (resource) {
      this.selectedCountry = resource;
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  onBlur(event: any) {
    this.focus = false;
    if (this.onTouchedCallback) {
      this.onTouchedCallback(this.selectedCountry);
    }
  }

  onFocus(event: any) {
    this.focus = true;
    if (this.onTouchedCallback) {
      this.onTouchedCallback(this.selectedCountry);
    }
  }
}
